import type { TableProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React, { useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import styled from 'styled-components'
import { gray } from 'sutra/tokens/colors'

import { SutraSearch } from 'components/ui/SutraSearch'
import { StyledTable } from 'features/catalog/features/table/catalog-table.styles'
import { matchesFocusSearch } from 'features/catalog/features/table/search-matching'
import { useBrandProductRows } from 'features/catalog/hooks/use-brand-product-rows'
import type { FocusListOption } from 'features/catalog/types'
import { buildProjectCatalogPathFromLens } from 'features/catalog/utils'
import type { FilterOption } from 'features/filters/types'
import { usePageKeyboardShortcuts } from 'hooks/keyboard-shortcuts'
import { useTableKeyboardNavigation } from 'hooks/use-table-keyboard-navigation'

type Props = {
  item: FocusListOption
  projectId: string
  defaultFilterList: FilterOption[]
}

/**
 * The product-hierarchy listing for a Brand-lens detail page — every product
 * with records under this brand, in the page's current date range/sidebar
 * filters. Mirrors `CatalogSourceDetailPanel`'s shape (search + `/` shortcut,
 * flat paginated table, row click navigating to the product) but reuses the
 * exact hook the main catalog table itself calls (`useCatalogTableRows`) with
 * `selectedLens: 'product_hierarchy'` and a copy of the live panel scoped down
 * to this brand — no new query, no new row-assembly code.
 */
export const CatalogBrandProductsPanel: React.FC<Props> = ({
  item,
  projectId,
  defaultFilterList,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchTerm, setSearchTerm] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  usePageKeyboardShortcuts(
    { '/': () => searchInputRef.current?.focus() },
    { respectInputs: true },
  )

  const { rows, isLoading } = useBrandProductRows({
    projectId,
    item,
    defaultFilterList,
  })

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows
    const searchLower = searchTerm.toLowerCase()
    return rows.filter((row) => matchesFocusSearch(row, searchLower))
  }, [rows, searchTerm])

  // Carries the current `?filters=` (and any other search params) onto the
  // product detail page — a plain `navigate(path)` would otherwise drop the
  // query string, and cmd-click/middle-click open a fresh tab with no
  // filter state to overlay from. Shared by row click and Enter-to-activate.
  const buildProductPath = (row: FocusListOption): string =>
    buildProjectCatalogPathFromLens(projectId, 'product_hierarchy', row.value) +
    location.search

  const {
    currentPage,
    pageSize,
    containerRef,
    onSearchKeyDown,
    onPageChange,
    onTableChange,
    getRowClassName,
  } = useTableKeyboardNavigation({
    rows: filteredRows,
    resetKey: searchTerm,
    pageSize: 25,
    onActivate: (row) => navigate(buildProductPath(row)),
  })

  const columns: ColumnsType<FocusListOption> = [
    {
      title: '#',
      key: 'number',
      width: 48,
      render: (_, __, index) => (
        <NumberCell>{(currentPage - 1) * pageSize + index + 1}</NumberCell>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: 'Records',
      dataIndex: 'n_posts',
      key: 'n_posts',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.n_posts - b.n_posts,
      render: (n_posts: number) => n_posts.toLocaleString(),
    },
  ]

  const handleTableChange: NonNullable<
    TableProps<FocusListOption>['onChange']
  > = (...args) => {
    onTableChange(args[3].currentDataSource)
  }

  return (
    <PanelContainer ref={containerRef}>
      <SearchWrapper>
        <SutraSearch
          ref={searchInputRef}
          placeholder="Search for product"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          onKeyDown={onSearchKeyDown}
          shortcutHint="/"
        />
      </SearchWrapper>
      <StyledTable
        dataSource={filteredRows}
        columns={columns}
        rowKey="value"
        size="small"
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          onChange: onPageChange,
        }}
        onChange={handleTableChange}
        rowClassName={getRowClassName}
        onRow={(row) => {
          const path = buildProductPath(row)
          return {
            style: { cursor: 'pointer' },
            onClick: (event) => {
              if (event.metaKey || event.ctrlKey) {
                window.open(path, '_blank', 'noopener,noreferrer')
                return
              }
              navigate(path)
            },
            onMouseDown: (event) => {
              if (event.button === 1) {
                window.open(path, '_blank', 'noopener,noreferrer')
              }
            },
          }
        }}
      />
    </PanelContainer>
  )
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SearchWrapper = styled.div`
  max-width: 400px;
`

const NumberCell = styled.span`
  color: ${gray[400]};
  font-variant-numeric: tabular-nums;
`
